/**
 * Base View Class (HTML-Based)
 * 
 * HTML-based alternative to canvas-based BaseView.
 * Renders views as DOM elements instead of canvas for better accessibility and simplicity.
 * 
 * Usage:
 * ```typescript
 * export class MyView extends BaseViewHTML {
 *   protected getContentHTML(): string {
 *     return `<div>My content</div>`;
 *   }
 *   
 *   protected render(): void {
 *     if (!this.element) return;
 *     this.element.innerHTML = this.getContentHTML();
 *   }
 * }
 * ```
 */

import type { ViewInstance, ViewGeometry, ViewMetadata } from './process.js';
import { bus } from '../core/events.js';
import { viewManager } from './view-manager.js';
import { GridPositionPreference } from '../grid-allocation-v2/types.js';
import { log } from '../services/logging/index.js';
import { designTokens, escapeHtml } from '../core/design-system.js';
import { isRestoringPersistedViews } from '../core/boot.js';
import { getViewGap, getContainerPadding, getStandardViewWidth, getDoubleViewWidth, getAvailableHeight, isMobileViewport, getVerticalPadding } from './layout-constants.js';

const viewGap = getViewGap(); // Gap between views (modest margin, like old grid system)

export abstract class BaseViewHTML {
  public readonly id: string;
  public readonly processId: string;
  protected geometry: ViewGeometry;
  protected metadata: ViewMetadata;

  // DOM element (owned by BaseViewHTML, in DOM)
  protected element: HTMLElement | null = null;
  private isMounted: boolean = false;
  private containerElement: HTMLElement | null = null;
  private isRuntimeSpawn: boolean = false; // Track if this view was spawned at runtime (not restored)
  
  // Drag state
  private isDragging: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private dragOffsetX: number = 0;
  private dragOffsetY: number = 0;
  private dragState: 'none' | 'dismiss' | 'attention' = 'none';
  private reorderIndicator: HTMLElement | null = null; // Visual indicator for insertion point
  
  // Resize state
  private isResizing: boolean = false;
  private resizeStartX: number = 0;
  private resizeStartY: number = 0;
  private resizeStartWidth: number = 0;
  private resizeStartHeight: number = 0;
  private resizable: boolean = false;
  
  // Geometry change callback - views can set this to react to size/position changes
  public onGeometryChange?: (geometry: ViewGeometry, metadata: ViewMetadata) => void;

  constructor(
    processId: string,
    viewId: string,
    options: {
      preferredRatio?: number;
      acceptableSizes?: Array<{ cols: number; rows: number }>;
      preferredPosition?: GridPositionPreference;
      priority?: number;
      showDismissButton?: boolean;
      resizable?: boolean;
      allowedPages?: number[];
      /** 
       * Supported sizes array: [0, 1, 2, 3]
       * 0 = Half height, normal width
       * 1 = Full height, normal width
       * 2 = Half height, double width
       * 3 = Full height, double width
       * First number in array is the default size
       */
      sizes?: number[]; // Array of supported sizes (0-3), first is default
      
      /** Is this view an overlay? (Skipped by layout allocator, fixed position) */
      isOverlay?: boolean;
    } = {}
  ) {
    this.processId = processId;
    this.id = viewId;

    // Parse sizes array: [0, 1, 2, 3] where:
    // 0 = Half height, normal width
    // 1 = Full height, normal width
    // 2 = Half height, double width
    // 3 = Full height, double width
    const sizes = options.sizes ?? [1]; // Default to [1] = full height, normal width
    const defaultSizeIndex = sizes[0] ?? 1;
    
    // Map size index to widthMode and heightMode
    const sizeToModes = (sizeIndex: number): { widthMode: 'standard' | 'double', heightMode: 'full' | 'half' } => {
      switch (sizeIndex) {
        case 0: return { widthMode: 'standard', heightMode: 'half' };
        case 1: return { widthMode: 'standard', heightMode: 'full' };
        case 2: return { widthMode: 'double', heightMode: 'half' };
        case 3: return { widthMode: 'double', heightMode: 'full' };
        default: return { widthMode: 'standard', heightMode: 'full' };
      }
    };
    
    const defaultModes = sizeToModes(defaultSizeIndex);
    const widthMode = defaultModes.widthMode;
    const heightMode = defaultModes.heightMode;
    
    // Calculate dimensions from viewport (unit-independent)
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const availableHeight = getAvailableHeight();
    
    const standardWidth = getStandardViewWidth();
    const doubleWidth = getDoubleViewWidth();
    
    // Calculate default dimensions based on modes
    const defaultWidth = widthMode === 'double' ? doubleWidth : standardWidth;
    // Half-height views need to account for vertical gap between stacked views
    // Subtract half the gap from each half-height view so they align with the bottom baseline
    const defaultHeight = heightMode === 'half' ? (availableHeight / 2) - (viewGap / 2) : availableHeight;

    // Initialize geometry
    this.geometry = {
      x: 0,
      y: 0,
      width: defaultWidth,
      height: defaultHeight
    };

    // Convert sizes array to sizeStates format for toggle handle
    const sizeStates = sizes.map(sizeIndex => sizeToModes(sizeIndex));

    // Initialize metadata
    this.metadata = {
      sizeConstraints: {
        acceptableSizes: options.acceptableSizes || [{ cols: 1, rows: 1 }]
      },
      preferredPosition: options.preferredPosition,
      priority: options.priority ?? 1,
      allowedPages: options.allowedPages,
      showDismissButton: options.showDismissButton ?? true,
      heightMode: heightMode,
      widthMode: widthMode,
      isOverlay: options.isOverlay
    };
    
    // Store resizable flag
    this.resizable = options.resizable ?? false;
    
    // Store size states for toggle resize handle (if multiple sizes supported)
    (this as any).sizeStates = sizeStates.length > 1 ? sizeStates : undefined;
    (this as any).currentSizeStateIndex = 0;
    (this as any).sizes = sizes; // Store original sizes array
  }

  /**
   * Convert to ViewInstance for registration
   */
  toViewInstance(): ViewInstance {
    return {
      id: this.id,
      processId: this.processId,
      kind: 'square' as const,
      geometry: this.geometry,
      metadata: this.metadata,
      data: {
        _baseViewInstance: this
      },
      destroy: () => {
        this.unmount();
      }
    };
  }

  /**
   * Get current geometry
   */
  getGeometry(): ViewGeometry {
    return { ...this.geometry };
  }

  /**
   * Get view metadata
   */
  getMetadata(): ViewMetadata {
    return this.metadata;
  }

  /**
   * Mount view to container
   */
  mount(container: HTMLElement): void {
    if (this.isMounted) {
      log.warn(`[BaseViewHTML] View ${this.id} is already mounted`);
      return;
    }

    this.containerElement = container;
    this.element = document.createElement('div');
    this.element.className = 'pivot-view';
    this.element.setAttribute('data-view-id', this.id);
    
    // Prevent click-through to background layer
    this.element.style.pointerEvents = 'auto';
    
    // Always start invisible by default
    this.element.style.opacity = '0';
    
    // Determine if this is a runtime spawn (not restored from storage)
    this.isRuntimeSpawn = !isRestoringPersistedViews;
    
    // Apply initial geometry
    this.updateGeometry();
    
    // Render content
    this.render();
    
    // Force a reflow to ensure browser registers opacity: 0, then set transition
    // This ensures the transition works properly
    void this.element.offsetHeight; // Force reflow
    
    // Now set the transition property
    this.element.style.transition = 'opacity 0.4s ease-in';
    
    // Fade in the view based on spawn type
    if (isRestoringPersistedViews) {
      // For views restored from persistent storage, fade in after 7-8 seconds (randomized for staggered effect)
      const fadeDelay = 7000 + Math.random() * 1000;
      setTimeout(() => {
        if (this.element) {
          this.element.style.opacity = '1';
        }
      }, fadeDelay);
    } else if (this.isRuntimeSpawn) {
      // For runtime spawns (user actions), fade in immediately but smoothly
      // Use requestAnimationFrame to ensure transition works properly
      requestAnimationFrame(() => {
        if (this.element) {
          this.element.style.opacity = '1';
        }
      });
    }
    // If neither condition is true, view stays invisible (shouldn't happen, but safe fallback)
    
    // Add dismiss button if needed (after render so it's not overwritten)
    if (this.metadata.showDismissButton) {
      this.addDismissButton();
    }
    
    // Add resize handle if resizable (after render so it's not overwritten)
    // Hide resize handle on mobile viewports
    // Resize handle removed - plus button in top-right corner now handles resizing
          
          // Append to container (will be moved to correct position by view-manager)
          if (this.element) {
            container.appendChild(this.element);
          }
          
          this.isMounted = true;
          
          // Setup drag handlers
          this.setupDragHandlers();
    
    log.debug(`[BaseViewHTML] Mounted view ${this.id}`);
  }
  
  /**
   * Get the DOM element (for external manipulation)
   */
  getElement(): HTMLElement | null {
    return this.element;
  }
  
  /**
   * Check if an element is interactive (should not trigger drag)
   */
  private isInteractiveElement(element: HTMLElement | null): boolean {
    if (!element) return false;
    
    // Check if it's an interactive element or inside one
    const interactiveSelectors = [
      'input',
      'textarea',
      'button',
      'select',
      'a',
      '[contenteditable="true"]',
      '.pivot-view-dismiss',
      '.todo-add-input',
      '.todo-add-button',
      '.todo-checkbox',
      '.todo-text',
      '.todo-delete',
      '.note-title',
      '.note-content',
      '.calendar-nav-prev',
      '.calendar-nav-next',
      '.calendar-nav-today',
      '.calendar-day',
      '.calendar-event',
      '.pivot-view-resize-handle'
    ];
    
    // Check if the element itself matches
    for (const selector of interactiveSelectors) {
      if (element.matches(selector)) {
        return true;
      }
    }
    
    // Check if element is inside an interactive element
    for (const selector of interactiveSelectors) {
      if (element.closest(selector)) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Setup drag handlers for view
   */
  private setupDragHandlers(): void {
    if (!this.element) return;
    
    let startX = 0;
    let startY = 0;
    let initialX = 0;
    
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // Don't drag if clicking on interactive elements or resize handle
      if (this.isInteractiveElement(target) || target.closest('.pivot-view-resize-handle')) {
        return;
      }
      
      // Don't drag if clicking on canvas elements (e.g., drawing canvas)
      if (target.tagName === 'CANVAS' || target.closest('canvas')) {
        return;
      }
      
      // Don't drag if already resizing
      if (this.isResizing) {
        return;
      }
      
      // Only start dragging on left mouse button
      if (e.button !== 0) {
        return;
      }
      
      e.preventDefault();
      e.stopPropagation();
      
      console.log(`[View Drag E2E] DRAG START: viewId=${this.id}, processId=${this.processId}, mousePos=(${e.clientX}, ${e.clientY})`);
      
      // Emit drag started event for metaball
      import('../core/events.js').then(({ bus }) => {
        bus.emit('view.drag.started', {
          viewId: this.id,
          processId: this.processId,
          timestamp: Date.now()
        });
      }).catch(() => {
        // Event bus not available, ignore
      });
      
      this.isDragging = true;
      startX = e.clientX;
      startY = e.clientY; // Track mouse Y for dismiss/attention gestures only
      initialX = this.geometry.x;
      
      // CRITICAL: Disable transitions immediately when drag starts
      if (this.element) {
        this.element.style.transition = 'none';
      }
      
      this.element?.classList.add('dragging');
      
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      // Safety: Global mouseup listener to ensure indicator is always removed
      // This catches cases where the view's mouseup handler might not fire
      const globalMouseUp = () => {
        this.forceRemoveReorderIndicator();
        document.removeEventListener('mouseup', globalMouseUp);
      };
      document.addEventListener('mouseup', globalMouseUp, { once: true });
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!this.isDragging || !this.element) return;
      
      const deltaX = e.clientX - startX;
      
      // Update position (only horizontal - Y is managed by allocator, not during drag)
      const newX = initialX + deltaX;
      const previousX = this.geometry.x;
      
      
      this.geometry.x = newX;
      // Y position is fixed by allocator - don't modify during drag
      this.updateGeometry();
      
      // Pre-compute reorder and show insertion indicator (only if dragging)
      if (this.isDragging) {
        this.updateReorderIndicator(newX);
      } else {
        // Safety: hide indicator if not dragging
        this.removeReorderIndicator();
      }
      
      // Determine drag state based on mouse Y movement (for dismiss/attention gestures)
      // Note: View Y position is fixed by allocator, but we track mouse Y for gesture detection
      const viewportHeight = window.innerHeight;
      const dismissThreshold = viewportHeight * 0.3; // 30% of viewport height
      const attentionThreshold = -viewportHeight * 0.2; // 20% upward
      
      // Check if dragging to metaball overlay (replaces AI visualization)
      // Use cached reference from App instance (passed via global)
      const aiElement = (window as any).__pivotAIVisualizationElement || 
                       (document.querySelector('#majaku-metaball-overlay-canvas') as HTMLElement);
      let isOverAI = false;
      if (aiElement) {
        const aiRect = aiElement.getBoundingClientRect();
        isOverAI = e.clientX >= aiRect.left && e.clientX <= aiRect.right &&
                   e.clientY >= aiRect.top && e.clientY <= aiRect.bottom;
      }
      
      // Update metaball drag state
      import('../services/majaku-privacy-background/metaball-drag-state.js').then(({ handleDragOverMetaball, handleDragLeaveMetaball, getMetaballDragState }) => {
        const previousState = getMetaballDragState();
        if (isOverAI) {
          handleDragOverMetaball();
          const newState = getMetaballDragState();
        } else {
          if (previousState === 'dragging-over') {
            handleDragLeaveMetaball();
            const newState = getMetaballDragState();
          }
        }
      }).catch(() => {
        // Metaball drag state not available, ignore
      });
      
      // Update drag state with enhanced visual feedback
      // Use mouse Y position relative to initial position for gestures (not view Y position)
      const mouseDeltaY = e.clientY - startY;
      const previousDragState = this.dragState;
      if (mouseDeltaY > dismissThreshold) {
        this.dragState = 'dismiss';
        this.element.classList.remove('drag-attention');
        this.element.classList.add('drag-dismiss');
        if (aiElement) aiElement.classList.remove('drag-over');
        
      } else if (mouseDeltaY < attentionThreshold || isOverAI) {
        this.dragState = 'attention';
        this.element.classList.remove('drag-dismiss');
        this.element.classList.add('drag-attention');
        if (aiElement && isOverAI) {
          aiElement.classList.add('drag-over');
        } else if (aiElement) {
          aiElement.classList.remove('drag-over');
        }
        
      } else {
        this.dragState = 'none';
        this.element.classList.remove('drag-dismiss', 'drag-attention');
        if (aiElement) aiElement.classList.remove('drag-over');
      }
    };
    
    const handleMouseUp = (e: MouseEvent) => {
      if (!this.isDragging) return;
      
      
      // Emit drag ended event for metaball
      import('../core/events.js').then(({ bus }) => {
        bus.emit('view.drag.ended', {
          viewId: this.id,
          processId: this.processId,
          timestamp: Date.now()
        });
      }).catch(() => {
        // Event bus not available, ignore
      });
      
      this.isDragging = false;
      this.element?.classList.remove('dragging');
      
      // CRITICAL: Always remove reorder indicator when mouse button is released
      // Use force remove to ensure it never gets stuck
      this.forceRemoveReorderIndicator();
      
      // Re-enable transitions after drag ends (will be applied on next updateGeometry)
      // Don't set it here - let updateGeometry handle it based on isDragging state
      
      // Handle drag actions
      if (this.dragState === 'dismiss') {
        // Remove reorder indicator before dismissing
        this.removeReorderIndicator();
        // Dismiss view
        bus.emit('process.view.dismiss', { processId: this.processId, viewId: this.id });
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        return;
      } else if (this.dragState === 'attention') {
        // Remove reorder indicator before attention
        this.removeReorderIndicator();
        // Bring to AI attention
        bus.emit('view.attention', { viewId: this.id });
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        return;
      }
      
      // Reset drag state
      this.dragState = 'none';
      this.element?.classList.remove('drag-dismiss', 'drag-attention');
      // Use cached reference from App instance (metaball overlay replaces AI visualization)
      const aiElement = (window as any).__pivotAIVisualizationElement || 
                       (document.querySelector('#majaku-metaball-overlay-canvas') as HTMLElement);
      if (aiElement) aiElement.classList.remove('drag-over');
      
      // Handle metaball drag end
      import('../services/majaku-privacy-background/metaball-drag-state.js').then(({ handleDragEnd, getMetaballDragState }) => {
        const stateBeforeEnd = getMetaballDragState();
        handleDragEnd();
        const stateAfterEnd = getMetaballDragState();
      }).catch(() => {
        // Metaball drag state not available, ignore
      });
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      // After drag ends, update order and reflow everything
      this.handleDragDrop();
      
    };
    
    this.element.addEventListener('mousedown', handleMouseDown);
  }

  /**
   * Unmount view from container
   */
  unmount(): void {
    // Clean up reorder indicator
    this.removeReorderIndicator();
    if (this.reorderIndicator && this.reorderIndicator.parentElement) {
      this.reorderIndicator.parentElement.removeChild(this.reorderIndicator);
      this.reorderIndicator = null;
    }
    
    if (!this.isMounted || !this.element) {
      return;
    }

    if (this.element.parentElement) {
      this.element.parentElement.removeChild(this.element);
    }

    this.element = null;
    this.containerElement = null;
    this.isMounted = false;
    
    log.debug(`[BaseViewHTML] Unmounted view ${this.id}`);
  }

  /**
   * Update view geometry (called when allocation changes)
   * 
   * CRITICAL: This method does NOT emit events or trigger re-allocation.
   * It only updates the DOM element's position/size. This prevents recursive
   * allocation chains when calculateNextPosition calls updateGeometry.
   */
  updateGeometry(newGeometry?: ViewGeometry): void {
    // Track if geometry actually changed (only if newGeometry is provided)
    let geometryChanged = false;
    if (newGeometry) {
      geometryChanged = (
        newGeometry.width !== this.geometry.width ||
        newGeometry.height !== this.geometry.height ||
        newGeometry.x !== this.geometry.x ||
        newGeometry.y !== this.geometry.y
      );
      this.geometry = { ...newGeometry };
    }

    if (!this.element) {
      // Still notify callback even if element doesn't exist yet (geometry was updated)
      if (geometryChanged && this.onGeometryChange) {
        this.onGeometryChange(this.geometry, this.metadata);
      }
      return;
    }
    
    const element = this.element; // TypeScript now knows element is non-null
    
    if (!element) {
      return; // Additional safety check
    }

    // Update element position and size
    // For horizontal scroll, use relative positioning within flex container
    // During drag, switch to absolute positioning
    if (this.isDragging) {
      element.style.position = 'absolute';
      element.style.transition = 'none'; // CRITICAL: No transitions during drag
      element.style.left = `${this.geometry.x}px`;
      element.style.top = `${this.geometry.y}px`;
      element.style.zIndex = '10000';
      element.style.transform = 'none';
      element.style.marginLeft = '0';
      element.style.marginRight = '0';
    } else {
      // Normal state: absolute positioning in 2D grid
      element.style.position = 'absolute';
      
      // Add smooth transition for position changes (but preserve opacity transition if present)
      if (!this.isDragging && !this.isResizing) {
        const existingTransition = element.style.transition;
        const hasOpacityTransition = existingTransition && existingTransition.includes('opacity');
        if (hasOpacityTransition) {
          // Preserve opacity transition and add position transitions
          element.style.transition = 'opacity 0.4s ease-in, left 0.3s cubic-bezier(0.4, 0, 0.2, 1), top 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        } else {
          // Only position transitions
          element.style.transition = 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1), top 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
        }
      } else {
        // During drag/resize, preserve opacity transition but remove position transitions
        const existingTransition = element.style.transition;
        const hasOpacityTransition = existingTransition && existingTransition.includes('opacity');
        if (hasOpacityTransition) {
          element.style.transition = 'opacity 0.4s ease-in';
        } else {
          element.style.transition = ''; // No transition during drag/resize
        }
      }
      
      element.style.left = `${this.geometry.x || 0}px`;
      element.style.top = `${this.geometry.y || 0}px`;
      element.style.marginLeft = '0';
      element.style.marginRight = '0';
      element.style.zIndex = 'auto';
      element.style.transform = 'none';
    }
    
    // Update element size (element is guaranteed non-null at this point)
    if (!this.element) return;
    
    const el = this.element as HTMLElement; // Type assertion after null check
    el.style.width = `${this.geometry.width ?? 400}px`;
    el.style.height = `${this.geometry.height ?? 300}px`;
    el.style.flexShrink = '0'; // Prevent shrinking in flex container
    
    // Update opacity if present in geometry (but preserve fade-in state)
    // Only set from geometry if element doesn't have a transition (meaning fade-in is complete)
    if (this.geometry.opacity !== undefined && this.geometry.opacity !== null) {
      const hasTransition = el.style.transition.includes('opacity');
      if (!hasTransition || el.style.opacity === '1') {
        // Only override if no opacity transition is active, or if already fully visible
        el.style.opacity = this.geometry.opacity.toString();
      }
    }
    
    // Update dismiss button position and mask if geometry changed
    // This ensures the hole and button stay aligned when card is resized
    if (this.metadata.showDismissButton) {
      this.updateDismissButtonPosition();
      this.updateIndexDisplayPosition();
    }
    
    // Notify callback if geometry changed (size or position)
    if (geometryChanged && this.onGeometryChange) {
      this.onGeometryChange(this.geometry, this.metadata);
      // Also mark as dirty to trigger re-render
      this.markDirty();
    }
  }
  
  /**
   * Update dismiss button position and mask to match current geometry
   * Called when geometry changes to keep hole and button aligned
   */
  private updateDismissButtonPosition(): void {
    if (!this.element) return;
    
    const button = this.element.querySelector('.pivot-view-dismiss') as HTMLElement;
    if (!button) return; // Button not created yet
    
    // Use same calculation as addDismissButton()
    const cardWidth = this.geometry.width || 400;
    const cardHeight = this.geometry.height || 300;
    const holeOffset = 20;
    const holeRadius = 10;
    const holeX = cardWidth - holeOffset;
    const holeY = holeOffset;
    
    // Plus position - to the left of the circle
    const plusSize = 10;
    const plusThickness = plusSize * 0.6;
    const starSpacing = 30;
    const starX = holeX - starSpacing;
    const starY = holeY;
    
    // Create mask using canvas (same approach as addDismissButton)
    const canvas = document.createElement('canvas');
    canvas.width = cardWidth;
    canvas.height = cardHeight;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      // Fill with black (opaque)
      ctx.fillStyle = 'black';
      ctx.fillRect(0, 0, cardWidth, cardHeight);
      
      // Clear circle area (transparent hole)
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(holeX, holeY, holeRadius, 0, Math.PI * 2);
      ctx.fill();
      
      // Clear plus area (transparent hole)
      ctx.beginPath();
      this.tracePlusShape(ctx, starX, starY, plusSize, plusThickness);
      ctx.fill();
      
      // Update mask with canvas
      const maskDataUrl = canvas.toDataURL();
      this.element.style.maskImage = `url("${maskDataUrl}")`;
      this.element.style.webkitMaskImage = `url("${maskDataUrl}")`;
    } else {
      // Fallback to just circle mask if canvas not available
      const gradientSize = Math.max(cardWidth, cardHeight) * 3;
      const circleMask = `radial-gradient(circle ${holeRadius}px at ${holeX}px ${holeY}px, transparent ${holeRadius - 1}px, black ${holeRadius}px, black ${gradientSize}px)`;
      this.element.style.maskImage = circleMask;
      this.element.style.webkitMaskImage = circleMask;
    }
    
    // Update button position to match mask hole exactly
    button.style.left = `${holeX - holeRadius}px`;
    button.style.top = `${holeY - holeRadius}px`;
    
    // Update star button position and highlight (now rendering as a plus symbol)
    const starButton = this.element.querySelector('.pivot-view-star') as HTMLElement;
    const starHighlight = this.element.querySelector('.pivot-view-star-highlight') as HTMLElement;
    if (starButton) {
      const starButtonSize = plusSize * 2.2;
      starButton.style.left = `${starX - starButtonSize / 2}px`;
      starButton.style.top = `${starY - starButtonSize / 2}px`;
      
      // Update star highlight gradient position and mask if it exists
      if (starHighlight) {
        // Recalculate star points for the mask
        // Update gradient position
        starHighlight.style.background = `radial-gradient(circle at ${starX}px ${starY}px, rgba(255, 215, 0, 0.4) 0%, rgba(255, 215, 0, 0.2) ${plusSize}px, transparent ${plusSize * 2}px)`;
        
        // Recreate star-only mask
        const starMaskCanvas = document.createElement('canvas');
        starMaskCanvas.width = cardWidth;
        starMaskCanvas.height = cardHeight;
        const starMaskCtx = starMaskCanvas.getContext('2d');
        
        if (starMaskCtx) {
          starMaskCtx.fillStyle = 'black';
          starMaskCtx.fillRect(0, 0, cardWidth, cardHeight);
          starMaskCtx.globalCompositeOperation = 'destination-out';
          starMaskCtx.beginPath();
          this.tracePlusShape(starMaskCtx, starX, starY, plusSize, plusThickness);
          starMaskCtx.fill();
          
          const starMaskDataUrl = starMaskCanvas.toDataURL();
          starHighlight.style.maskImage = `url("${starMaskDataUrl}")`;
          starHighlight.style.webkitMaskImage = `url("${starMaskDataUrl}")`;
        }
      }
    }
  }

  /**
   * Update index display position to match current geometry
   * Called when geometry changes to keep index aligned with dismiss button
   */
  private updateIndexDisplayPosition(): void {
    if (!this.element) return;
    
    const indexDisplay = this.element.querySelector('.pivot-view-index') as HTMLElement;
    if (!indexDisplay) return; // Index display not created yet
    
    // Use same calculation as addDismissButton()
    const cardWidth = this.geometry.width || 400;
    const holeOffset = 20;
    const holeRadius = 10;
    const holeX = cardWidth - holeOffset;
    const holeY = holeOffset;
    const indexOffset = 35; // Space between index and button
    
    // Update index display position (just to the left of dismiss button)
    indexDisplay.style.left = `${holeX - holeRadius - indexOffset}px`;
    indexDisplay.style.top = `${holeY - 8}px`;
  }

  /**
   * Mark view as needing re-render
   */
  markDirty(): void {
    if (this.isMounted) {
      this.render();
    }
  }

  /**
   * Get content area (accounting for padding)
   */
  protected getContentArea(): { x: number; y: number; width: number; height: number } {
    const padding = 16; // Standard padding
    return {
      x: padding,
      y: padding,
      width: (this.geometry.width || 0) - padding * 2,
      height: (this.geometry.height || 0) - padding * 2
    };
  }

  /**
   * Abstract method: Get HTML content for this view
   */
  protected abstract getContentHTML(): string;

  /**
   * Wrap view content with standard padding/layout. Views can override to customize.
   */
  protected wrapContent(innerHTML: string): string {
    const padding = designTokens?.padding?.lg || '24px';
    const gap = designTokens?.padding?.md || '16px';
    return `
      <div class="pivot-view-content" style="
        width: 100%;
        height: 100%;
        padding: ${padding};
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        gap: ${gap};
      ">
        ${innerHTML}
      </div>
    `;
  }

  /**
   * Render view content (can be overridden for custom rendering)
   */
  protected render(): void {
    if (!this.element) {
      return;
    }

    // Set base styles (two-tone design: dark gray card, white content)
    this.element.style.backgroundColor = designTokens.cardBackground;
    this.element.style.borderRadius = designTokens.radius.xl;
    this.element.style.border = `1px solid ${designTokens.borderColor}`;
    this.element.style.overflow = 'hidden';
    this.element.style.color = designTokens.contentColor;
    
    // Preserve dismiss button, star button, and star highlight when re-rendering
    const existingDismissButton = this.element.querySelector('.pivot-view-dismiss');
    const existingStarButton = this.element.querySelector('.pivot-view-star');
    const existingStarHighlight = this.element.querySelector('.pivot-view-star-highlight');
    
    // Set content
    this.element.innerHTML = this.wrapContent(this.getContentHTML());
    
    // Remove star button/highlight if view is not resizable or on mobile (check again after innerHTML in case it persists)
    const currentStarButton = this.element.querySelector('.pivot-view-star');
    const currentStarHighlight = this.element.querySelector('.pivot-view-star-highlight');
    if ((currentStarButton || currentStarHighlight) && (!this.resizable || isMobileViewport())) {
      if (currentStarButton) currentStarButton.remove();
      if (currentStarHighlight) currentStarHighlight.remove();
    }
    
    // Resize handle removed - plus button in top-right corner now handles resizing
    
    // Re-add dismiss button and star button/highlight if they existed
    // Only re-add star button if view is resizable and not on mobile
    if (existingDismissButton && this.metadata.showDismissButton) {
      this.addDismissButton(); // This will conditionally add the plus button and highlight based on resizable state
    } else if ((existingStarButton || existingStarHighlight) && this.resizable && !isMobileViewport() && this.metadata.showDismissButton) {
      // If only star button/highlight existed (unlikely but possible), re-add dismiss button
      this.addDismissButton();
    }
  }

  /**
   * Toggle to the next size state with smooth animation
   */
  private toggleSizeState(): void {
    const sizeStates = (this as any).sizeStates as Array<{
      widthMode: 'standard' | 'double';
      heightMode: 'full' | 'half';
    }>;
    
    if (!sizeStates || sizeStates.length < 2) return;
    
    // Cycle to next state
    (this as any).currentSizeStateIndex = ((this as any).currentSizeStateIndex + 1) % sizeStates.length;
    const nextState = sizeStates[(this as any).currentSizeStateIndex];
    
    if (!this.element || !nextState) return;
    
    // Calculate dimensions for the next state (unit-independent, viewport-based)
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const availableHeight = getAvailableHeight();
    const standardWidth = Math.floor(viewportWidth * 0.2);
    
    const newWidth = nextState.widthMode === 'double' ? standardWidth * 2 : standardWidth;
    // Half-height views need to account for vertical gap between stacked views
    // Subtract half the gap from each half-height view so they align with the bottom baseline
    const newHeight = nextState.heightMode === 'half' ? (availableHeight / 2) - (viewGap / 2) : availableHeight;
    
    // Add smooth transition to the element
    this.element.style.transition = 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), height 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
    
    // Update geometry
    this.updateGeometry({
      x: this.geometry.x || 0,
      y: this.geometry.y || 0,
      width: newWidth,
      height: newHeight
    });
    
    // Update metadata to reflect new size
    this.metadata.widthMode = nextState.widthMode;
    this.metadata.heightMode = nextState.heightMode;
    
    // Notify callback of geometry and metadata change
    if (this.onGeometryChange) {
      this.onGeometryChange(this.geometry, this.metadata);
    }
    
    // Remove transition after animation completes to allow normal dragging/resizing
    setTimeout(() => {
      if (this.element) {
        this.element.style.transition = '';
      }
    }, 300);
    
    // Adjust views around this resized view without repositioning it
    // This keeps the view in place and just adjusts surrounding views
    import('./horizontal-allocator.js').then(({ adjustViewsAroundResizedView }) => {
      adjustViewsAroundResizedView(this);
    });
  }


  /**
   * Add dismiss button (circular hole that reveals background)
   * Creates an actual circular hole in the card using CSS mask
   */
  private addDismissButton(): void {
    if (!this.element) {
      return;
    }

    // Calculate hole position - use geometry dimensions (same for both mask and button)
    // Both mask and button must use EXACTLY the same coordinate system
    const cardWidth = this.geometry.width || 400;
    const cardHeight = this.geometry.height || 300;
    
    const holeOffset = 20; // Distance from edges
    const holeRadius = 10; // 10px radius
    const holeX = cardWidth - holeOffset; // X position from left edge (for mask)
    const holeY = holeOffset; // Y position from top edge (for mask)
    
    // Plus position - to the left of the circle
    const plusSize = 10; // Half-length of each arm
    const plusSpacing = 30; // Space between plus and circle
    const plusX = holeX - plusSpacing; // X position of plus center
    const plusY = holeY; // Y position of plus center (same as circle)
    const plusThickness = plusSize * 0.6;
    
    // Create the actual hole in the card using CSS mask
    // We need a mask that is opaque (black) everywhere EXCEPT the hole (transparent)
    // Use a radial gradient: black (opaque) at edges, transparent (hole) at center
    // Position the gradient at the hole location
    // Sharp edge: 1px transition zone for hard cutout edge
    const gradientSize = Math.max(cardWidth, cardHeight) * 3; // Large enough to cover the card
    
    // Radial gradient: transparent at center (the hole), black everywhere else (opaque card)
    // Sharp edge: 1px transition for clean cutout
    const circleMask = `radial-gradient(circle ${holeRadius}px at ${holeX}px ${holeY}px, transparent ${holeRadius - 1}px, black ${holeRadius}px, black ${gradientSize}px)`;
    
    // Create mask using canvas - draw black background, then clear circle and plus areas
    // This creates transparent holes in the alpha channel, matching the radial gradient approach
    const canvas = document.createElement('canvas');
    canvas.width = cardWidth;
    canvas.height = cardHeight;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      // Fill with black (opaque)
      ctx.fillStyle = 'black';
      ctx.fillRect(0, 0, cardWidth, cardHeight);
      
      // Clear circle area (transparent hole)
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(holeX, holeY, holeRadius, 0, Math.PI * 2);
      ctx.fill();
      
      // Clear plus area (transparent hole) only if view is resizable
      if (this.resizable && !isMobileViewport()) {
        ctx.beginPath();
        this.tracePlusShape(ctx, plusX, plusY, plusSize, plusThickness);
        ctx.fill();
      }
      
      // Use canvas as mask image
      const maskDataUrl = canvas.toDataURL();
      this.element.style.maskImage = `url("${maskDataUrl}")`;
      this.element.style.webkitMaskImage = `url("${maskDataUrl}")`;
    } else {
      // Fallback to just circle mask if canvas not available
      this.element.style.maskImage = circleMask;
      this.element.style.webkitMaskImage = circleMask;
    }
    
    // Create the interactive button element (transparent, invisible until hover)
    const button = document.createElement('button');
    button.className = 'pivot-view-dismiss';
    button.setAttribute('aria-label', 'Close view');
    
    // Position button at EXACTLY the same location as the mask hole
    // Use the same coordinates: holeX from left, holeY from top
    // Center the button on the hole by offsetting by radius
    button.style.cssText = `
      position: absolute;
      left: ${holeX - holeRadius}px;
      top: ${holeY - holeRadius}px;
      width: ${holeRadius * 2}px;
      height: ${holeRadius * 2}px;
      border-radius: 50%;
      background: transparent;
      border: none;
      cursor: pointer;
      z-index: 10;
      pointer-events: auto;
      transition: all 0.2s ease;
      /* No visible styling - invisible until hover */
      box-shadow: none;
      opacity: 0;
    `;
    
    button.addEventListener('click', (e) => {
      e.stopPropagation();
      bus.emit('process.view.dismiss', { processId: this.processId, viewId: this.id });
    });
    
    button.addEventListener('mouseenter', () => {
      // On hover: subtle glow to indicate interactivity
      button.style.opacity = '1';
      button.style.boxShadow = `0 0 16px rgba(102, 204, 255, 0.5)`;
    });
    
    button.addEventListener('mouseleave', () => {
      // Return to invisible state
      button.style.opacity = '0';
      button.style.boxShadow = 'none';
    });
    
    this.element.appendChild(button);
    
    // Create interactive element for the plus (similar to dismiss button) only if view is resizable
    if (this.resizable && !isMobileViewport()) {
      const starButton = document.createElement('button');
      starButton.className = 'pivot-view-star';
      starButton.setAttribute('aria-label', 'Plus affordance');
      
      const starButtonSize = plusSize * 2.2; // Slightly larger than plus to ensure full coverage
      starButton.style.cssText = `
        position: absolute;
        left: ${plusX - starButtonSize / 2}px;
        top: ${plusY - starButtonSize / 2}px;
        width: ${starButtonSize}px;
        height: ${starButtonSize}px;
        background: transparent;
        border: none;
        cursor: pointer;
        z-index: 10;
        pointer-events: auto;
        transition: all 0.2s ease;
        box-shadow: none;
        opacity: 0;
      `;
      
      // Create highlight element that covers entire card and uses a plus-only mask
      // This will show a glow effect only through the plus cutout
      const starHighlight = document.createElement('div');
      starHighlight.className = 'pivot-view-star-highlight';
      
      starHighlight.style.cssText = `
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 9;
        opacity: 0;
        transition: opacity 0.2s ease;
      `;
      
      // Create a mask that only has the plus as transparent (not the circle)
      // This way the highlight only shows through the plus shape
      const starMaskCanvas = document.createElement('canvas');
      starMaskCanvas.width = cardWidth;
      starMaskCanvas.height = cardHeight;
      const starMaskCtx = starMaskCanvas.getContext('2d');
      
      let starMaskDataUrl = '';
      if (starMaskCtx) {
        // Fill with black (opaque)
        starMaskCtx.fillStyle = 'black';
        starMaskCtx.fillRect(0, 0, cardWidth, cardHeight);
        
        // Clear only the plus area (transparent hole)
        starMaskCtx.globalCompositeOperation = 'destination-out';
        starMaskCtx.beginPath();
        this.tracePlusShape(starMaskCtx, plusX, plusY, plusSize, plusThickness);
        starMaskCtx.fill();
        
        starMaskDataUrl = starMaskCanvas.toDataURL();
      }
      
      starHighlight.style.background = `radial-gradient(circle at ${plusX}px ${plusY}px, rgba(255, 215, 0, 0.4) 0%, rgba(255, 215, 0, 0.2) ${plusSize}px, transparent ${plusSize * 2}px)`;
      
      // Apply the plus-only mask to the highlight so it only shows through the plus cutout
      if (starMaskDataUrl) {
        starHighlight.style.maskImage = `url("${starMaskDataUrl}")`;
        starHighlight.style.webkitMaskImage = `url("${starMaskDataUrl}")`;
      }
      
      this.element.appendChild(starHighlight);
      
      starButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Trigger the same logic as the resize toggle button
        this.toggleSizeState();
      });
      
      starButton.addEventListener('mouseenter', () => {
        // Show highlight that follows the plus shape via mask
        starHighlight.style.opacity = '1';
      });
      
      starButton.addEventListener('mouseleave', () => {
        starHighlight.style.opacity = '0';
      });
      
      this.element.appendChild(starButton);
    }
    
    /*
    // Add index position display (just to the left of dismiss button)
    const indexDisplay = document.createElement('div');
    indexDisplay.className = 'pivot-view-index';
    
    // Get index from state.viewOrder
    const updateIndexDisplay = () => {
      import('../core/state.js').then(({ state }) => {
        const viewOrder = state.viewOrder || [];
        const index = viewOrder.indexOf(this.id);
        if (index !== -1) {
          indexDisplay.textContent = `${index}`;
          indexDisplay.style.display = 'block';
        } else {
          indexDisplay.style.display = 'none';
        }
      });
    };
    
    // Update index display initially and when order changes
    updateIndexDisplay();
    
    // Listen for state updates to refresh index
    import('../core/events.js').then(({ bus }) => {
      bus.on('state.update', (payload: unknown) => {
        if (typeof payload === 'object' && payload !== null && 'key' in payload && payload.key === 'viewOrder') {
          updateIndexDisplay();
        }
      });
    });
    
    // Position just to the left of dismiss button
    const indexOffset = 35; // Space between index and button
    indexDisplay.style.cssText = `
      position: absolute;
      left: ${holeX - holeRadius - indexOffset}px;
      top: ${holeY - 8}px;
      font-size: 11px;
      color: rgba(255, 255, 255, 0.6);
      font-family: monospace;
      pointer-events: none;
      user-select: none;
      z-index: 9;
      font-weight: 500;
    `;
    
    this.element.appendChild(indexDisplay);
    */
  }

  private tracePlusShape(
    ctx: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    armLength: number,
    thickness: number
  ): void {
    const halfThickness = thickness / 2;
    const halfSize = armLength;
    ctx.rect(centerX - halfThickness, centerY - halfSize, thickness, halfSize * 2);
    ctx.rect(centerX - halfSize, centerY - halfThickness, halfSize * 2, thickness);
  }
  
  /**
   * Handle drag drop - simple: update order and reflow everything
   * No complex collision avoidance, no vertical snapping, just reflow from scratch
   */
  private handleDragDrop(): void {
    
    if (!this.element) {
      return;
    }
    
    // Get all other views to find which one we're closest to (horizontally)
    const allViews = viewManager.getAllViews();
    const currentX = this.geometry.x || 0;
    const thisCenterX = currentX + (this.geometry.width || 400) / 2;
    
    import('../core/state.js').then(({ state, setState, updateState }) => {
      const viewOrder = state.viewOrder || [];
      const previousOrder = [...viewOrder];
      const thisIndex = viewOrder.indexOf(this.id);
      
      
      if (thisIndex === -1) {
        // Not in order - just reflow
        import('./horizontal-allocator.js').then(({ allocateAllViews }) => {
          allocateAllViews();
        });
        return;
      }
      
      // Find the view whose center is closest to our center (horizontally)
      let closestView: { view: BaseViewHTML; viewId: string; centerX: number; index: number } | null = null;
      let minDistance = Infinity;
      
      for (const [viewId, view] of allViews.entries()) {
        if (viewId === this.id) continue;
        
        const geom = view.getGeometry();
        const otherCenterX = (geom.x || 0) + (geom.width || 400) / 2;
        const distance = Math.abs(thisCenterX - otherCenterX);
        
        if (distance < minDistance) {
          const index = viewOrder.indexOf(viewId);
          if (index !== -1) {
            minDistance = distance;
            closestView = { view, viewId, centerX: otherCenterX, index };
          }
        }
      }
      
      
      // If we're close enough to another view, update order
      const threshold = (this.geometry.width || 400) * 1.5;
      if (closestView && minDistance < threshold) {
        
        // Remove this view from its current position
        const filteredOrder = viewOrder.filter(id => id !== this.id);
        
        // Insert before or after closest view based on position
        const insertBefore = thisCenterX < closestView.centerX;
        if (insertBefore) {
          // Insert before closest view
          const insertIndex = closestView.index > thisIndex ? closestView.index - 1 : closestView.index;
          filteredOrder.splice(insertIndex, 0, this.id);
        } else {
          // Insert after closest view
          const insertIndex = closestView.index > thisIndex ? closestView.index : closestView.index + 1;
          filteredOrder.splice(insertIndex, 0, this.id);
        }
        
        
        // Update order
        if (!state.viewOrder) {
          setState('viewOrder', filteredOrder);
        } else {
          updateState('viewOrder', () => filteredOrder);
        }
        
        // Check if order actually changed
        const orderChanged = JSON.stringify(previousOrder) !== JSON.stringify(filteredOrder);
        if (orderChanged) {
        } else {
        }
      } else {
      }
      
      // Always reflow everything from scratch (simple, deterministic)
      import('./horizontal-allocator.js').then(({ allocateAllViews }) => {
        allocateAllViews();
      });
    });
  }

  /**
   * Update reorder indicator during drag - shows where view will be inserted
   */
  private updateReorderIndicator(currentX: number): void {
    // Safety: only show indicator when dragging
    if (!this.isDragging) {
      this.removeReorderIndicator();
      return;
    }
    if (!this.element) return;
    
    const thisCenterX = currentX + (this.geometry.width || 400) / 2;
    const allViews = viewManager.getAllViews();
    
    import('../core/state.js').then(({ state }) => {
      const viewOrder = state.viewOrder || [];
      const thisIndex = viewOrder.indexOf(this.id);
      
      if (thisIndex === -1) {
        this.removeReorderIndicator();
        return;
      }
      
      // Find the view whose center is closest to our center (horizontally)
      let closestView: { view: BaseViewHTML; centerX: number; index: number; x: number; width: number } | null = null;
      let minDistance = Infinity;
      
      for (const [viewId, view] of allViews.entries()) {
        if (viewId === this.id) continue;
        
        const geom = view.getGeometry();
        const otherCenterX = (geom.x || 0) + (geom.width || 400) / 2;
        const distance = Math.abs(thisCenterX - otherCenterX);
        
        if (distance < minDistance) {
          const index = viewOrder.indexOf(viewId);
          if (index !== -1) {
            minDistance = distance;
            closestView = { 
              view, 
              centerX: otherCenterX, 
              index,
              x: geom.x || 0,
              width: geom.width || 400
            };
          }
        }
      }
      
      const threshold = (this.geometry.width || 400) * 1.5;
      
      // If we're close enough to another view, show insertion indicator
      if (closestView && minDistance < threshold) {
        // Determine insertion point: before or after closest view
        const insertBefore = thisCenterX < closestView.centerX;
        
        // Calculate indicator position
        let indicatorX: number;
        if (insertBefore) {
          // Insert before - show line at left edge of closest view
          indicatorX = closestView.x;
        } else {
          // Insert after - show line at right edge of closest view
          indicatorX = closestView.x + closestView.width;
        }
        
        this.showReorderIndicator(indicatorX, insertBefore);
      } else {
        // Not close enough - hide indicator
        if (closestView) {
        } else {
        }
        this.removeReorderIndicator();
      }
    });
  }

  /**
   * Show reorder indicator at specified X position
   */
  private showReorderIndicator(x: number, insertBefore: boolean): void {
    // Get or create indicator element
    const wasCreated = !this.reorderIndicator;
    if (!this.reorderIndicator) {
      this.reorderIndicator = document.createElement('div');
      this.reorderIndicator.className = 'pivot-view-reorder-indicator';
      
      // Add to layer-content container (same parent as views)
      const contentWrapper = document.querySelector('.layer-content') as HTMLElement;
      if (contentWrapper) {
        contentWrapper.appendChild(this.reorderIndicator);
      } else {
        document.body.appendChild(this.reorderIndicator);
      }
    }
    
    // Calculate dimensions to match horizontal layout margins
    const verticalPadding = getVerticalPadding(); // Same as horizontal-allocator
    const viewGap = getViewGap(); // Same as horizontal-allocator
    const viewportHeight = window.innerHeight;
    const indicatorTop = verticalPadding;
    const indicatorHeight = viewportHeight - (verticalPadding * 2);
    
    // Offset by half the view gap (negative if inserting before, positive if after)
    const gapOffset = insertBefore ? -(viewGap / 2) : (viewGap / 2);
    const indicatorX = x + gapOffset;
    
    // Update position and dimensions
    this.reorderIndicator.style.cssText = `
      position: absolute;
      top: ${indicatorTop}px;
      left: ${indicatorX}px;
      width: 2px;
      height: ${indicatorHeight}px;
      background: rgba(255, 255, 255, 0.8);
      pointer-events: none;
      z-index: 9999;
      box-shadow: 0 0 4px rgba(255, 255, 255, 0.5);
    `;
    
    this.reorderIndicator.style.display = 'block';
    
    if (wasCreated) {
    } else {
    }
  }

  /**
   * Remove reorder indicator
   */
  private removeReorderIndicator(): void {
    if (this.reorderIndicator && this.reorderIndicator.style.display !== 'none') {
      this.reorderIndicator.style.display = 'none';
      // Don't remove from DOM - just hide it (we'll reuse it)
    }
  }
  
  /**
   * Force remove reorder indicator (safety cleanup)
   * Called when mouse button is released to ensure indicator never gets stuck
   */
  private forceRemoveReorderIndicator(): void {
    this.removeReorderIndicator();
    // Also check if any other views have stuck indicators (safety net)
    const allIndicators = document.querySelectorAll('.pivot-view-reorder-indicator');
    allIndicators.forEach(indicator => {
      (indicator as HTMLElement).style.display = 'none';
    });
  }

  /**
   * Escape HTML (re-exported from design system for convenience)
   */
  protected escapeHtml(text: string): string {
    return escapeHtml(text);
  }
}

